import { useEffect, useState } from "react";
import { Toast, ToastViewport, T, type ToastType } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

/* NO import from `../support/**` or `./access` here, deliberately.
 *
 * `access.ts` imports THIS module, so anything this module imports statically
 * joins an access.ts-rooted cycle. A static \`openReportProblem\` import pulled
 * `support/ReportProblem` → `lib/audit`, and audit.ts calls `onTenantChange()`
 * at module scope — which ran while access.ts was still on its own import line,
 * before `_tenantChangeSubs` existed. Result: `undefined.add`, and the entire
 * app rendered the error boundary.
 *
 * The seam below inverts it: the support module REGISTERS its opener when it
 * mounts, and this file knows nothing about it. */
let _reportHandler: (() => void) | null = null;
/** Called by `ReportProblemHost` on mount; pass null to withdraw the action
 *  (signed out, or the user lacks `support.report`). */
export function setToastReportHandler(fn: (() => void) | null): void { _reportHandler = fn; }

/**
 * Imperative toast dispatcher for the ESM app — the real replacement for the
 * prototype's `window.toast`. A module-level store + subscription (same pattern
 * as access.ts); `<ToastHost/>` (mounted once in App) renders the stack.
 *
 *   import { toast } from "../shell/toast";
 *   toast({ tone: "success", title: "Saved", subtitle: "Your changes are stored." });
 */
export interface ToastInput {
  /** Severity — prototype name. Mapped to the jrni-ui Toast `type`. */
  tone?: ToastType;
  title: string;
  /** Prototype name for the body line. */
  subtitle?: string;
  description?: string;
  /** Prototype alias for the body line — same slot as `subtitle`/`description`.
   * Accepted because call sites reach for it naturally (Orders collect/refund);
   * one of the three wins, first non-empty. */
  message?: string;
  autoDismissMs?: number;
  /** Offer "Report this" on the toast (2026-09-21, Nick — support intake).
   *  Defaults to ON for `error`: the moment something fails is the moment the
   *  reporter still knows what they were doing, and asking them to find the
   *  profile menu afterwards loses most of that. Pass `false` to suppress it
   *  on a failure the user cannot act on. */
  report?: boolean;
}
interface ToastEntry extends ToastInput { id: number; }

let _toasts: ToastEntry[] = [];
let _seq = 1;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });

export function toast(input: ToastInput): number {
  const id = _seq++;
  _toasts = [..._toasts, { ...input, id }];
  _emit();
  return id;
}
export function dismissToast(id: number) {
  _toasts = _toasts.filter((t) => t.id !== id);
  _emit();
}

export function ToastHost() {
  const tr = useT();
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    _subs.add(f);
    return () => { _subs.delete(f); };
  }, []);
  if (_toasts.length === 0) return null;
  return (
    <ToastViewport placement="bottom-right">
      {_toasts.map((t) => {
        const body = t.subtitle || t.description || t.message;
        const offerReport = (t.report ?? t.tone === "error") && !!_reportHandler;
        return (
          <Toast
            key={t.id}
            type={t.tone || "info"}
            layout="notification"
            title={t.title}
            description={offerReport ? (
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
                {body ? <span>{body}</span> : null}
                {/* ui-raw: an inline text action inside a Toast body. Toast has
                    no action slot, and a full Button in a 4.5-second toast
                    outweighs the message it sits under. */}
                <button type="button" onClick={() => { dismissToast(t.id); _reportHandler?.(); }}
                  style={{ border: "none", background: "none", padding: 0, font: "inherit", fontWeight: 600, color: T.p1, textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer" }}>
                  {tr("support.report.toastAction", "Report this")}
                </button>
              </span>
            ) : body}
            dismissible
            autoDismissMs={t.autoDismissMs ?? (offerReport ? 9000 : 4500)}
            onDismiss={() => dismissToast(t.id)}
          />
        );
      })}
    </ToastViewport>
  );
}
